import React from "react";
import { Box, Chip, CircularProgress, Skeleton } from "@mui/material";
import useFieldFilterData from "../../hooks/use-filter-data";

interface TileSelectorProps {
  selectedTile: number | null;
  onTileSelect: (tile: number) => void;
}

/**
 * TileSelector displays tiles from A to Z for single selection.
 * Provides a clean, tag-based interface for selecting data categories.
 */
const TileSelector: React.FC<TileSelectorProps> = ({
  selectedTile,
  onTileSelect,
}) => {
  const { data, error, isLoading } = useFieldFilterData();

  if (error) {
    return <Box sx={{ p: 2, color: "error.main" }}>Error loading tiles</Box>;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(0, 0, 0, 0.02)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={20} />
        <Box sx={{ display: "flex", gap: 0.75, flex: 1 }}>
          {Array.from({ length: 10 }, (_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rounded"
              width={40}
              height={28}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "rgba(0, 0, 0, 0.02)",
        overflowX: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          minWidth: "max-content",
        }}
      >
        {data?.map((tile) => {
          const isSelected = selectedTile === tile.id;
          return (
            <Chip
              key={tile.id}
              label={tile.description}
              onClick={() => onTileSelect(tile.id)}
              color={isSelected ? "primary" : "default"}
              variant={isSelected ? "filled" : "outlined"}
              sx={{
                fontWeight: isSelected ? 600 : 500,
                fontSize: "0.75rem",
                height: 28,
                minWidth: 28,
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "& .MuiChip-label": {
                  px: 1,
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 1,
                },
                ...(isSelected && {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }),
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default TileSelector;
